import React, { useEffect, useState } from 'react';
import { Button, TwIconButton, ToggleSwitch } from 'components/common';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import {
  FaTimes, FaShareAlt, FaUserFriends, FaSitemap, FaUserShield, FaEnvelope, FaPaperPlane,
} from 'react-icons/fa';
import { shareAsset } from 'redux/drive/driveSlice';
import { activeEmployeesByNameDebounce, departmentsDebounce } from 'helpers/employeeOptionHelper';
import { getFileMeta } from './fileTypes';

const TAB_DEFS = [
  { key: 'employees',   label: 'People',      icon: FaUserFriends },
  { key: 'departments', label: 'Departments', icon: FaSitemap },
];

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 8,
    borderColor: state.isFocused ? '#2563eb' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(37,99,235,0.15)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#2563eb' : '#cbd5e1' },
    fontSize: 13,
  }),
  placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: 13 }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#eff6ff',
    border: '1px solid #dbeafe',
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({ ...base, color: '#1e40af', fontSize: 12, fontWeight: 600 }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#1e40af',
    ':hover': { backgroundColor: '#dbeafe', color: '#1e3a8a' },
  }),
  menu: (base) => ({ ...base, zIndex: 1000000, fontSize: 13 }),
};

const DriveShareForm = ({ show, onClose, asset }) => {
  const dispatch = useDispatch();
  const { control, register, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    defaultValues: {
      employee_names: [],
      department_ids: [],
      emp_notify_people: true,
      set_owner: false,
      emp_optional_message: '',
      dep_notify_people: true,
      dep_optional_message: '',
    },
  });
  const [tabActive, setTabActive] = useState('employees');
  const [submitting, setSubmitting] = useState(false);

  const meta = asset ? getFileMeta(asset) : null;
  const Icon = meta?.icon;
  const iconColor = meta?.color;

  useEffect(() => {
    if (show) {
      reset();
      setTabActive('employees');
      setSubmitting(false);
    }
  }, [show, reset]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => e.key === 'Escape' && !submitting && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [show, submitting, onClose]);

  const onSubmit = async (data) => {
    if (!asset) return;
    const payload = tabActive === 'employees'
      ? {
          share_type: 'employees',
          employee_names: data.employee_names,
          set_owner: data.set_owner,
          notify_people: data.emp_notify_people,
          optional_message: data.emp_optional_message,
        }
      : {
          share_type: 'departments',
          department_ids: data.department_ids,
          notify_people: data.dep_notify_people,
          optional_message: data.dep_optional_message,
        };

    setSubmitting(true);
    try {
      await dispatch(shareAsset({ assetId: asset.id, data: payload })).unwrap();
      toast.success('Shared successfully');
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to share');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const modal = (
    <div
      onClick={submitting ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 999999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        backgroundColor: 'rgba(15,23,42,0.45)',
        animation: 'driveShareFadeIn 180ms ease',
      }}
    >
      <style>{`
        @keyframes driveShareFadeIn { from { background-color: rgba(15,23,42,0); } to { background-color: rgba(15,23,42,0.45); } }
        @keyframes driveSharePopIn { from { transform: translateY(8px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
      `}</style>

      <form
        onSubmit={handleSubmit(onSubmit)}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560,
          backgroundColor: '#ffffff',
          borderRadius: 14,
          boxShadow: '0 20px 60px rgba(15,23,42,0.18), 0 4px 12px rgba(15,23,42,0.08)',
          overflow: 'hidden',
          animation: 'driveSharePopIn 200ms cubic-bezier(0.16, 1, 0.3, 1)',
          display: 'flex', flexDirection: 'column', maxHeight: 'max(240px, calc(100dvh - 32px))',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 10,
          flexShrink: 0,
        }}>
          <div style={{
            width: 32, height: 32, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#eff6ff', color: '#2563eb',
            border: '1px solid #dbeafe', borderRadius: 8,
          }}>
            <FaShareAlt size={13} />
          </div>
          <h3 style={{
            flex: 1, margin: 0,
            fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.4,
          }}>
            Share
          </h3>
          <TwIconButton icon={FaTimes} size="md" variant="ghost" onClick={onClose} disabled={submitting} title="Close" />
        </div>

        {/* Asset banner */}
        {asset && (
          <div style={{
            margin: '12px 16px 0',
            padding: '10px 12px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 36, height: 36, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              color: iconColor,
            }}>
              {Icon && <Icon size={18} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: 0, fontSize: 11, fontWeight: 600, color: '#64748b',
                textTransform: 'uppercase', letterSpacing: 0.4,
              }}>
                Sharing
              </p>
              <p style={{
                margin: '2px 0 0', fontSize: 13, fontWeight: 700, color: '#0f172a',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }} title={asset.name}>
                {asset.name}
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          margin: '12px 16px 0',
          padding: 4,
          backgroundColor: '#f1f5f9',
          borderRadius: 10,
          display: 'flex', gap: 4,
          flexShrink: 0,
        }}>
          {TAB_DEFS.map((tab) => {
            const TabIcon = tab.icon;
            const active = tabActive === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setTabActive(tab.key)}
                disabled={submitting}
                style={{
                  appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
                  flex: 1, padding: '8px 10px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  backgroundColor: active ? '#ffffff' : 'transparent',
                  color: active ? '#0f172a' : '#64748b',
                  border: '1px solid',
                  borderColor: active ? '#e2e8f0' : 'transparent',
                  borderRadius: 8,
                  fontSize: 12, fontWeight: 700, lineHeight: 1,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'background-color 120ms ease, color 120ms ease',
                }}
              >
                <TabIcon size={11} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {tabActive === 'employees' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <FieldBlock
                label="People"
                required
                icon={FaUserFriends}
                error={errors.employee_names?.message}
              >
                <Controller
                  control={control}
                  name="employee_names"
                  rules={{ required: tabActive === 'employees' ? 'Please select at least one person' : false }}
                  render={() => (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      isMulti
                      loadOptions={activeEmployeesByNameDebounce}
                      placeholder="Search people…"
                      closeMenuOnSelect={false}
                      classNamePrefix="react-select"
                      styles={reactSelectStyles}
                      onChange={(opts) =>
                        setValue('employee_names', (opts || []).map((o) => o.value), { shouldValidate: true })
                      }
                      isDisabled={submitting}
                    />
                  )}
                />
              </FieldBlock>

              <ToggleBlock
                control={control}
                name="emp_notify_people"
                icon={FaEnvelope}
                title="Notify people by email"
                description="They'll receive an email about the shared item."
                setValue={setValue}
                disabled={submitting}
              />

              <ToggleBlock
                control={control}
                name="set_owner"
                icon={FaUserShield}
                title="Transfer ownership"
                description="Make the recipient the new owner of this item."
                setValue={setValue}
                disabled={submitting}
              />

              <TextareaBlock
                label="Message (optional)"
                placeholder="Add a message…"
                {...register('emp_optional_message')}
                disabled={submitting}
              />
            </div>
          )}

          {tabActive === 'departments' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <FieldBlock
                label="Departments"
                required
                icon={FaSitemap}
                error={errors.department_ids?.message}
              >
                <Controller
                  control={control}
                  name="department_ids"
                  rules={{ required: tabActive === 'departments' ? 'Please select at least one department' : false }}
                  render={() => (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      isMulti
                      loadOptions={departmentsDebounce}
                      placeholder="Search departments…"
                      closeMenuOnSelect={false}
                      classNamePrefix="react-select"
                      styles={reactSelectStyles}
                      onChange={(opts) =>
                        setValue('department_ids', (opts || []).map((o) => o.value), { shouldValidate: true })
                      }
                      isDisabled={submitting}
                    />
                  )}
                />
              </FieldBlock>

              <ToggleBlock
                control={control}
                name="dep_notify_people"
                icon={FaEnvelope}
                title="Notify members by email"
                description="Members of the department will receive an email."
                setValue={setValue}
                disabled={submitting}
              />

              <TextareaBlock
                label="Message (optional)"
                placeholder="Add a message…"
                {...register('dep_optional_message')}
                disabled={submitting}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
          flexShrink: 0,
        }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="info" size="sm" type="submit" leftIcon={FaPaperPlane} loading={submitting}>
            Share
          </Button>
        </div>
      </form>
    </div>
  );

  return createPortal(modal, document.body);
};

const FieldBlock = ({ label, required, icon: Icon, error, children }) => (
  <div>
    <label style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: 12, fontWeight: 700, color: '#334155',
      marginBottom: 6,
    }}>
      {Icon && <Icon size={11} style={{ color: '#94a3b8' }} />}
      <span>{label}</span>
      {required && <span style={{ color: '#dc2626' }}>*</span>}
    </label>
    {children}
    {error && (
      <p style={{ margin: '4px 0 0', fontSize: 11, color: '#dc2626', fontWeight: 500 }}>
        {error}
      </p>
    )}
  </div>
);

FieldBlock.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  icon: PropTypes.elementType,
  error: PropTypes.string,
  children: PropTypes.node,
};

const ToggleBlock = ({ control, name, icon: Icon, title, description, setValue, disabled }) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
      }}>
        <div style={{
          width: 28, height: 28, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          color: '#475569',
        }}>
          <Icon size={11} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{title}</p>
          {description && (
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
              {description}
            </p>
          )}
        </div>
        <ToggleSwitch
          value={!!field.value}
          onChange={(val) => setValue(name, val, { shouldValidate: true })}
          disabled={disabled}
          size="sm"
        />
      </div>
    )}
  />
);

ToggleBlock.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  setValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const TextareaBlock = React.forwardRef(({ label, ...rest }, ref) => (
  <div>
    <label style={{
      display: 'block',
      fontSize: 12, fontWeight: 700, color: '#334155',
      marginBottom: 6,
    }}>
      {label}
    </label>
    <textarea
      ref={ref}
      rows={3}
      style={{
        width: '100%',
        padding: '8px 10px',
        fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5,
        color: '#0f172a',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        outline: 'none',
        resize: 'vertical',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#2563eb';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.15)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = '#e2e8f0';
        e.currentTarget.style.boxShadow = 'none';
      }}
      {...rest}
    />
  </div>
));

TextareaBlock.displayName = 'TextareaBlock';
TextareaBlock.propTypes = {
  label: PropTypes.string.isRequired,
};

DriveShareForm.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  asset: PropTypes.object,
};

export default DriveShareForm;
